import styled from '@emotion/styled';
import { useEffect, useRef } from 'react';
import videojs from 'video.js';
import Player from 'video.js/dist/types/player';
import 'video.js/dist/video-js.css';

export interface VideoJSOptions {
    autoplay?: boolean | 'play' | 'muted' | 'any';
    controls?: boolean;
    muted?: boolean;
    playsinline?: boolean;
    poster?: string;
    preload?: 'metadata' | 'none' | 'auto';
    sources?: {
        src?: string;
        type: string;
    }[];
    onReady?: (player: Player) => void;
    onDispose?: (player: Player) => void;
}

interface Props {
    options?: VideoJSOptions;
    style?: React.CSSProperties;
}

const Video = styled.video`
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;

    video {
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: center;
    }

    .vjs-poster img {
        object-fit: cover;
        object-position: center;
    }
`;

export const VideoJS = ({ options, style }: Props) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const playerRef = useRef<Player | null>(null);

    useEffect(() => {
        if (!videoRef.current) return;

        if (!playerRef.current) {
            playerRef.current = videojs(videoRef.current, options, function () {
                options?.onReady?.(this);
            });
        } else {
            const player = playerRef.current;
            player.autoplay(options?.autoplay);
            player.controls(options?.controls);
            player.muted(options?.muted);
            player.playsinline(options?.playsinline);
            player.poster(options?.poster);
            player.preload(options?.preload);
        }
    }, [options]);

    useEffect(() => {
        const player = playerRef.current;

        return () => {
            if (player && !player.isDisposed()) {
                options?.onDispose?.(playerRef.current!);
                player.dispose();
                playerRef.current = null;
            }
        };
    }, []);

    return (
        <Video
            data-vjs-player
            ref={videoRef}
            className='video-js vjs-default-skin'
            style={style}
            poster={options?.poster}
        />
    );
};
